import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { api } from '../../services/api';
import { useAuthStore, type User } from '../../store/authStore';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type LoginForm = z.infer<typeof loginSchema>;

interface LoginResponse {
  data: {
    access_token: string;
    user: User;
  };
}

export default function LoginPage(): React.ReactElement {
  const { t } = useTranslation('auth');
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm): Promise<void> => {
    setApiError('');
    try {
      const response = await api.post<LoginResponse>('/v1/auth/login', data);
      login(response.data.user, response.data.access_token);
      navigate('/app');
    } catch {
      setApiError(t('errors.invalidCredentials'));
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center pt-20 px-4">
      <div className="card bg-base-100 shadow-xl w-full max-w-md">
        <div className="card-body">
          <h2 className="card-title text-3xl font-bold justify-center mb-4">
            {t('login.title')}
          </h2>

          {apiError && (
            <div className="alert alert-error mb-4">
              <span>{apiError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('login.email')}</span>
              </label>
              <input
                type="email"
                className={`input input-bordered w-full ${errors.email ? 'input-error' : ''}`}
                {...register('email')}
              />
              {errors.email && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.email.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('login.password')}</span>
              </label>
              <input
                type="password"
                className={`input input-bordered w-full ${errors.password ? 'input-error' : ''}`}
                {...register('password')}
              />
              {errors.password && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.password.message}</span>
                </label>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full mt-2 mb-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? <span className="loading loading-spinner loading-sm" /> : t('login.submit')}
            </button>
          </form>

          <div className="text-center mt-2">
            <RouterLink to="/forgot-password" className="link link-primary text-sm">
              {t('login.forgotPassword')}
            </RouterLink>
            <p className="text-sm mt-2">
              {t('login.noAccount')}{' '}
              <RouterLink to="/register" className="link link-primary">
                {t('login.register')}
              </RouterLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
